import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Heart,
  Share2,
  Users,
  Wand2,
  Star,
  Copy,
  Loader2,
  Sparkles,
  GitBranch,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SiteFooter from '../components/SiteFooter';
import AuthDialog from '../components/AuthDialog';
import { Toaster } from '../components/ui/toaster';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENT_MAP = {
  'cyan-magenta': 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
  'silver-slate': 'linear-gradient(135deg, #e2e8f0 0%, #64748b 100%)',
  'emerald-lime': 'linear-gradient(135deg, #10b981 0%, #a3e635 100%)',
  'amber-crimson': 'linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)',
  'sky-lavender': 'linear-gradient(135deg, #38bdf8 0%, #c084fc 100%)',
  'violet-gold': 'linear-gradient(135deg, #8b5cf6 0%, #fbbf24 100%)',
};
const gradientFor = (k) => GRADIENT_MAP[k] || GRADIENT_MAP['cyan-magenta'];

export default function SoulDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [soul, setSoul] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/souls/${slug}`);
        if (r.ok) setSoul(await r.json());
        else setSoul(null);
      } catch {
        setSoul(null);
      }
      setLoading(false);
    })();
  }, [slug]);

  const activate = () => {
    if (!soul) return;
    try {
      localStorage.setItem('atw_soul', soul.system_prompt);
      localStorage.setItem('atw_soul_meta', JSON.stringify({
        name: soul.name, emoji: soul.emoji, slug: soul.slug,
      }));
      fetch(`${API}/souls/${soul.slug}/use`, { method: 'POST' }).catch(() => {});
      setActivated(true);
      toast({
        title: `${soul.emoji || '✨'} ${soul.name} activated`,
        description: 'The AI now speaks in this personality.',
      });
      setTimeout(() => navigate('/build'), 900);
    } catch (e) {
      toast({ title: 'Could not activate', description: e.message });
    }
  };

  const like = async () => {
    if (!soul) return;
    try {
      const r = await fetch(`${API}/souls/${soul.slug}/like`, { method: 'POST' });
      const d = await r.json();
      if (r.ok) setSoul((s) => ({ ...s, likes: d.likes }));
      else toast({ title: 'Already liked', description: d.detail || 'Come back tomorrow!' });
    } catch { /* noop */ }
  };

  const share = () => {
    const url = `${BACKEND_URL}/api/share/soul/${soul?.slug}`;
    try {
      navigator.clipboard.writeText(url);
      toast({ title: 'Share link copied', description: 'Paste it into Twitter/LinkedIn — the persona card previews automatically.' });
    } catch {
      toast({ title: 'Share URL', description: url });
    }
  };

  const copyPrompt = () => {
    try {
      navigator.clipboard.writeText(soul.system_prompt);
      toast({ title: 'Prompt copied', description: 'Paste it anywhere you like.' });
    } catch { /* noop */ }
  };

  if (loading) {
    return (
      <div className="relative bg-[#030303] text-white min-h-screen">
        <Navbar onGetStarted={() => setAuthOpen(true)} />
        <div className="pt-40 flex items-center justify-center text-white/60">
          <Loader2 size={22} className="animate-spin mr-2" /> Loading soul…
        </div>
        <Toaster />
      </div>
    );
  }

  if (!soul) {
    return (
      <div className="relative bg-[#030303] text-white min-h-screen">
        <Navbar onGetStarted={() => setAuthOpen(true)} />
        <div className="pt-40 max-w-xl mx-auto px-6 text-center">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">Not found</div>
          <h1 className="mt-3 text-4xl font-display font-black text-white">This soul wandered off.</h1>
          <p className="mt-3 text-white/60">We couldn't find a soul at <span className="font-mono text-white">/souls/{slug}</span>.</p>
          <div className="mt-6 flex justify-center gap-2">
            <Link to="/souls" className="rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-2.5 transition inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Back to library
            </Link>
          </div>
        </div>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="relative bg-[#030303] text-white min-h-screen overflow-hidden">
      <Navbar onGetStarted={() => setAuthOpen(true)} />

      {/* Ambient glow that matches soul gradient */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full blur-3xl opacity-25"
        style={{ background: gradientFor(soul.gradient) }}
      />

      <section className="relative pt-32 pb-6 px-6 lg:px-10 max-w-4xl mx-auto">
        <Link
          to="/souls"
          data-testid="soul-detail-back"
          className="inline-flex items-center gap-1.5 text-[13px] text-white/60 hover:text-white transition"
        >
          <ArrowLeft size={13} /> All souls
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 relative rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]"
          data-testid="soul-detail-card"
        >
          {/* Header banner */}
          <div
            className="h-40 relative overflow-hidden"
            style={{ background: gradientFor(soul.gradient) }}
          >
            <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{
              background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35), transparent 45%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.4), transparent 45%)',
            }} />
            {soul.featured && (
              <div className="absolute top-4 right-4 rounded-full bg-black/50 backdrop-blur-md border border-white/20 px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-widest text-white flex items-center gap-1.5">
                <Star size={11} className="text-amber-300" fill="currentColor" /> Featured
              </div>
            )}
          </div>

          <div className="px-6 md:px-8 pb-8 -mt-14 relative">
            {/* Icon + title */}
            <div className="flex items-start gap-4">
              <div
                className="h-24 w-24 rounded-2xl bg-black/70 backdrop-blur-md border border-white/20 flex items-center justify-center text-5xl shrink-0"
                data-testid="soul-detail-emoji"
              >
                {soul.emoji || '✨'}
              </div>
              <div className="pt-16 flex-1 min-w-0">
                <h1 className="text-3xl md:text-4xl font-display font-black tracking-tight text-white leading-tight">
                  {soul.name}
                </h1>
                <p className="mt-2 text-[14.5px] text-white/70 leading-relaxed">
                  {soul.tagline}
                </p>
              </div>
            </div>

            {/* Author + stats */}
            <div className="mt-5 flex flex-wrap items-center gap-4 text-[12px] text-white/55">
              <span className="inline-flex items-center gap-1.5">
                <Users size={12} /> by{' '}
                {soul.author_handle ? (
                  <Link to={`/@${soul.author_handle}`} className="text-white hover:text-cyan-300 transition">
                    {soul.author_name}
                  </Link>
                ) : soul.author_link ? (
                  <a href={soul.author_link} target="_blank" rel="noreferrer" className="text-white hover:text-cyan-300 transition">
                    {soul.author_name}
                  </a>
                ) : (
                  <span className="text-white">{soul.author_name}</span>
                )}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Heart size={12} /> {soul.likes} likes
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Wand2 size={12} /> {soul.uses} uses
              </span>
              {(soul.remix_count || 0) > 0 && (
                <span className="inline-flex items-center gap-1.5" data-testid="soul-detail-remix-count">
                  <GitBranch size={12} className="text-[#00F0FF]" /> {soul.remix_count} remixes
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Sparkles size={12} className="text-[#00F0FF]" /> {new Date(soul.created_at).toLocaleDateString()}
              </span>
            </div>

            {/* Remix source chip (if this soul is itself a remix) */}
            {soul.remixed_from && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-md px-3 py-1.5 text-[12px] text-white/70" data-testid="soul-detail-remix-source">
                <GitBranch size={12} className="text-[#00F0FF]" />
                <span>Remix of</span>
                <Link
                  to={`/souls/${soul.remixed_from}`}
                  className="text-white hover:text-cyan-300 transition font-medium"
                >
                  {soul.remix_source_name || soul.remixed_from}
                </Link>
              </div>
            )}

            {/* System prompt */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">System prompt</div>
                <button
                  onClick={copyPrompt}
                  data-testid="soul-detail-copy-prompt"
                  className="text-[11.5px] text-white/60 hover:text-white flex items-center gap-1 transition"
                >
                  <Copy size={11} /> Copy prompt
                </button>
              </div>
              <pre className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-[13.5px] leading-[1.7] text-white/80 font-sans">
                {soul.system_prompt}
              </pre>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col md:flex-row items-stretch md:items-center gap-3">
              <button
                onClick={activate}
                disabled={activated}
                data-testid="soul-detail-activate"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl text-black font-semibold text-[15px] py-4 hover:scale-[1.01] transition disabled:opacity-70"
                style={{ background: gradientFor(soul.gradient) }}
              >
                {activated ? (
                  <><Loader2 size={16} className="animate-spin" /> Opening builder…</>
                ) : (
                  <><Wand2 size={16} /> Use this SOUL in the builder <ArrowRight size={15} /></>
                )}
              </button>
              <Link
                to={`/souls?remix=${soul.slug}`}
                data-testid="soul-detail-remix"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-4 transition"
                title="Fork this SOUL — the submit form opens pre-filled from here"
              >
                <GitBranch size={15} /> Remix
              </Link>
              <button
                onClick={like}
                data-testid="soul-detail-like"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-4 transition"
              >
                <Heart size={15} /> Like
              </button>
              <button
                onClick={share}
                data-testid="soul-detail-share"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-4 transition"
              >
                <Share2 size={15} /> Share
              </button>
            </div>

            <div className="mt-4 text-[11.5px] text-white/40 text-center">
              Tapping <span className="text-white/70 font-mono">Use this SOUL</span> stores it locally and opens the builder — the AI's next reply will speak in this personality.
            </div>
          </div>
        </motion.div>
      </section>

      <div className="h-16" />
      <SiteFooter />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <Toaster />
    </div>
  );
}
